import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import MarginField from '@/components/margin/MarginField';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useMspBaseCatalogue, useUpdateFilterPack } from '@/services/api/hooks/catalog';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { CatalogueProvider } from '@/services/catalogue/CatalogueService';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const ManageBaseCatalogueGlobalMarginModalFields = z.object({
  value: z.string()
});

type ManageBaseCatalogueGlobalMarginModalFields = z.infer<typeof ManageBaseCatalogueGlobalMarginModalFields>;

export const ManageBaseCatalogueGlobalMarginModal: FC<ModalProps<{ mspId: string }>> = props => {
  const role = useRole();

  const ManageBaseCatalogueGlobalMarginForm = () => {
    const mspId = props.props?.mspId;
    if (!mspId) throw new Error('Missing msp id');

    const [baseCatalogue] = useMspBaseCatalogue(mspId);

    const updateFilterPack = useUpdateFilterPack();

    const methods = useForm<ManageBaseCatalogueGlobalMarginModalFields>({
      resolver: zodResolver(ManageBaseCatalogueGlobalMarginModalFields),
      defaultValues: { value: String(baseCatalogue.filterPack.globalMarginModifier) }
    });

    const onSubmit = async (data: ManageBaseCatalogueGlobalMarginModalFields) => {
      await updateFilterPack.mutateAsync({
        ...baseCatalogue.filterPack,
        marginFilterIds: baseCatalogue.filterPack.marginFilters.map(filter => filter.id),
        productFilterIds: baseCatalogue.filterPack.productFilters.map(filter => filter.id),
        globalMarginModifier: Number(data.value)
      });
      toast.success('Successfully updated global margin');
      props.close();
    };

    return (
      <Form methods={methods} onSubmit={onSubmit} className="flex w-full flex-col gap-4 py-2">
        <MarginField id="value" />
        <Button
          loading={updateFilterPack.isLoading}
          className="w-fit place-self-end"
          variant={ButtonVariant.Primary}
          type="submit"
        >
          Update
        </Button>
      </Form>
    );
  };

  return (
    <Modal className="md:max-w-2xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Global margin</h1>
        </Modal.Controls>
        <p className="mt-0.5 leading-tight">
          Global margin is the default margin applied to all products. This margin can be overridden by other settings
          like criteria or product filters.
        </p>

        <Suspense fallback={<Loading />}>
          <CatalogueProvider configuration={{ for: RoleType.Admin, as: role.type }}>
            <ManageBaseCatalogueGlobalMarginForm />
          </CatalogueProvider>
        </Suspense>
      </Card>
    </Modal>
  );
};
